import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function AuthPage({ page, csrfToken }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get('returnTo') || '/';

    const isSignup = (page === 'signup' || page === 'signup/desktop');
    const isLogin = (page === 'login');
    const isDesktop = (page === 'signup/desktop');

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const url = isSignup ? '/auth/signup' : '/auth/login';
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-csrf-token': csrfToken,
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Login failed');
                setLoading(false);
                return;
            }

            window.location.href = data?.redirect || returnTo;
        } catch {
            setError('Something went wrong. Please try again.');
            setLoading(false);
        }
    }

    return (
        <div className='flex-1 h-full bg-[#111] lg:bg-[#070707] overflow-auto custom-scroll'>
            <div className='min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr]'>
                <div className='flex flex-col justify-center min-h-[480px] lg:min-h-0 px-7 py-[42px] lg:px-16 lg:py-14 bg-[url(/static/images/auth-bg.png)] bg-cover bg-center bg-no-repeat'>
                    <div className='max-w-[460px]'>
                        {isDesktop ? (
                            <div className='inline-flex items-center gap-2 text-[13px] font-bold text-green1 tracking-[0.01em] px-3.5 py-2 bg-[#03d46e21] rounded-full border border-[#03d46e47]'>
                                <span className='w-1.5 h-1.5 rounded-full bg-green1 shadow-[0_0_8px_rgba(3, 212, 110, 0.7)]' />
                                Coming from JPEGmini Desktop
                            </div>
                        ) : (
                            <div className='flex items-center h-6'>
                                <div className='flex-center px-3 py-1 gap-1 rounded-full bg-green1/20 opacity-80 text-xs11 font-medium text-green1'>
                                    <img src='/static/images/icons/zap.svg' className='w-3 h-3 svg-green1' />
                                    COMPRESSION
                                </div>
                                <div className='flex-center px-3 py-1 gap-1 rounded-full bg-[#0c0c0d]/40 border border-white/20 opacity-80 text-xs11 font-medium text-white1/70'>
                                    AI FRAMING
                                </div>
                                <div className='flex-center px-3 py-1 gap-1 rounded-full bg-[#0c0c0d]/40 border border-white/20 opacity-80 text-xs11 font-medium text-white1/70'>
                                    CLIENT DELIVERY
                                </div>
                            </div>
                        )}

                        <div className='mt-[22px] text-[54px] text-[#eef0f5] font-semibold leading-[104%] tracking-[-1.5px]'>
                            {isDesktop ? (<>
                                Your videos,<br />
                                now in the <span className='text-[#13d67a]'>cloud.</span>
                            </>) : (<>
                                Compress.<br />
                                Reframe.<br />
                                <span className='text-[#13d67a]'>Deliver.</span>
                            </>)}
                        </div>

                        <div className={`mt-4 ${isDesktop ? '' : 'w-[360px]'} text-[#eef0f5]/60 text-sm leading-[175%]`}>
                            {isDesktop ? (<>
                                JPEGmini Desktop handles your photos.
                                The Cloud handles your videos — much faster processing, AI reframing, and instant client delivery.
                            </>) : (<>
                                Patent-pending compression cuts file size in half with
                                <span className='text-[#c2c8d8] font-semibold'> zero quality loss. </span>
                                AI reframing converts 16:9 to 9:16 in one click.
                                Instant delivery links — everything your clients need, nothing they don't.
                            </>)}
                        </div>

                        {isDesktop ? (<>
                            <div className='mt-8 grid gap-3.5'>
                                {[
                                    'Process videos in minutes — no more overnight exports',
                                    'Compress videos up to 50% smaller — same quality',
                                    'AI reframes 16:9 to 9:16 vertical in one click',
                                    'Send clients a single link — no re-uploading anywhere',
                                ].map((item, i) => (
                                    <div key={i} className='flex gap-3 items-start text-neutral-100 text-[15px] leading-[1.4]'>
                                        <span className='shrink-0 flex-center w-5 h-5 rounded-full bg-[#03d46e38] text-green1 text-xs11 font-black'>✓</span>
                                        {item}
                                    </div>
                                ))}
                            </div>

                            <div className='mt-9 inline-flex items-center flex-wrap gap-2.5 px-4 py-2.5 rounded-full bg-black/45 border border-white/12 text-[#f0f0f0] text-[13px] font-bold backdrop-blur-xs'>
                                Upload <span className='text-green1'>→</span>
                                Compress <span className='text-green1'>→</span>
                                Reframe <span className='text-green1'>→</span>
                                Deliver
                            </div>
                        </>) : (
                            <div className='mt-10 w-[380px] h-[69px] flex rounded-[14px] bg-[#0b0c0f]/55 border border-white/10 backdrop-filter-[blur(8px)]'>
                                <div className='flex flex-col justify-center items-start gap-1 px-5 border-r border-white/7'>
                                    <div className='text-[#5e6378] text-[10px] tracking-[1px]'>AVG SIZE SAVED</div>
                                    <div className='text-[#13d67a]'>-52%</div>
                                </div>
                                <div className='flex flex-col justify-center items-start gap-1 px-5'>
                                    <div className='text-[#5e6378] text-[10px] tracking-[1px]'>AI REFRAME</div>
                                    <div className='text-[#eef0f5]'>16:9 → 9:16</div>
                                </div>
                                <div className='flex flex-col justify-center items-start gap-1 px-5 border-l border-white/7'>
                                    <div className='text-[#5e6378] text-[10px] tracking-[1px]'>CREATORS</div>
                                    <div className='text-[#eef0f5]'>47k+</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className='bg-[#111] flex-center px-[22px] py-10 lg:px-[34px] lg:py-12'>
                    <div className='w-full max-w-[408px]'>
                        <a href='/' title='JPEGmini Cloud' className='flex-center gap-2'>
                            <div className='flex-center w-8 h-8 rounded-lg bg-green1 text-[#111] text-[15px] font-extrabold'>Jm</div>
                            <div className='flex items-end gap-1'>
                                <div className='font-bold text-white1 leading-[1]'>JPEGmini</div>
                                <div className='text-white1/70 text-sm font-semibold tracking-[0.6px] leading-[1]'>CLOUD</div>
                            </div>
                        </a>

                        {isDesktop && (
                            <div className='mt-8 px-4 py-3.5 rounded-xl bg-[#03d46e0f] border border-[#03d46e40] text-[#dcf8e9] text-[13px] leading-[1.5]'>
                                <div className='flex items-center gap-2 mb-1 text-green1 font-bold text-[13px]'>
                                    <span className='w-4 h-4 rounded-full bg-[#03d46e33] inline-flex justify-center items-center text-xs11 font-black'>i</span>
                                    New account needed
                                </div>
                                Cloud is a separate product from JPEGmini Desktop, so your desktop login won't work here. Takes 30 seconds to set up.
                            </div>
                        )}


                        <div className='mt-6 text-center text-white text-[28px] font-semibold leading-[1.2]'>
                            {isSignup
                                ? isDesktop
                                    ? 'Create your free account'
                                    : <div>Start delivering<br />in minutes.</div>
                                : 'Welcome back.'
                            }
                        </div>

                        <div className='mt-2 text-center text-white/55 text-sm leading-[140%]'>
                            {isSignup
                                ? isDesktop
                                    ? 'No credit card. No trial timer.'
                                    : 'Create your free account. No credit card needed.'
                                : 'Log in to your JPEGmini Cloud account.'
                            }
                        </div>

                        {isDesktop && (
                            <div className='mt-3.5 px-4 py-3.5 rounded-[10px] bg-white/3 border border-white/6'>
                                <div className='flex items-center justify-between mb-2.5'>
                                    <div className='px-2 py-0.5 rounded-full bg-[#03d46e26] text-green1 text-xs11 font-extrabold tracking-[0.06em]'>
                                        STARTER PLAN
                                    </div>
                                    <div className='text-white text-[13px] font-bold'>$0 · forever free</div>
                                </div>
                                <div className='grid gap-[7px]'>
                                    {[
                                        '1 active project',
                                        '5 GB max per video',
                                        'Unlimited compression',
                                        'Shareable links',
                                    ].map((item, i) => (
                                        <div key={i} className='flex items-center gap-2 text-[#d8d8d8] text-[13px] leading-[1.3]'>
                                            <div className='text-green1 font-black text-xs'>✓</div>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(error || searchParams.get('error')) && (
                            <div className='mt-8 p-3 rounded-lg bg-red-900/30 border border-red-800 text-red-300 text-sm'>
                                {error || 'Authentication failed. Please try again.'}
                            </div>
                        )}

                        <a
                            href={`/auth/google?returnTo=${encodeURIComponent(returnTo)}`}
                            className='mt-6 neutral-button2 w-full h-[52px]! justify-center gap-3! '
                        >
                            <svg width='20' height='20' viewBox='0 0 18 18'>
                                <path fill='#4285F4' d='M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z'/>
                                <path fill='#34A853' d='M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z'/>
                                <path fill='#FBBC05' d='M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z'/>
                                <path fill='#EA4335' d='M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z'/>
                            </svg>
                            {isLogin ? 'Continue with Google' : 'Sign up with Google'}
                        </a>

                        <div className='my-5 flex items-center gap-3.5'>
                            <div className='flex-grow h-px bg-[#3a3a3a]'></div>
                            <span className='text-xs text-[#6b6b6b]'>OR</span>
                            <div className='flex-grow h-px bg-[#3a3a3a]'></div>
                        </div>

                        <form className='' onSubmit={handleSubmit}>
                            <input
                                type='email'
                                placeholder='Email'
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                className='auth-input'
                                required
                                autoFocus
                            />

                            <input
                                type='password'
                                placeholder='Password'
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                className='auth-input mt-4'
                                required
                            />

                            {isLogin && (
                                <div className='mt-1 flex justify-end'>
                                    <a href='/forgot-password' className='text-xs text-mainblue-500 hover:text-mainblue-400'>
                                        Forgot password?
                                    </a>
                                </div>
                            )}

                            <button
                                type='submit'
                                className='mt-5 action-button w-full h-[52px] rounded-lg! text-base!'
                                disabled={loading}
                            >
                                {loading
                                    ? isLogin
                                        ? 'Logging in...'
                                        : 'Creating account...'
                                    : isLogin
                                        ? 'Log in'
                                        : 'Get started for free'
                                }
                            </button>
                        </form>

                        <div className='mt-4.5 text-center text-[13px] text-[#8a8a8a]'>
                            {isLogin
                                ? "Don't have an account?"
                                : 'Already have an account?'
                            }{' '}
                            <a href={isLogin ? '/signup' : '/login'} className='text-green1 hover:text-green2 font-medium'>
                                {isLogin ? 'Sign up free' : 'Log in'}
                            </a>
                        </div>

                        <div className='mt-6 text-center text-xs11 text-[#6b6b6b]'>
                            By creating an account, you agree to our{' '}
                            <a href='/terms' target='_blank' className='underline text-[#8a8a8a] hover:opacity-80'>Terms of Service</a>{' '}
                            and{' '}
                            <a href='/privacy' target='_blank' className='underline text-[#8a8a8a] hover:opacity-80'>Privacy Policy</a>.
                        </div>

                        <div className='mt-12 flex'>
                            <div className='text-[#6b6b6b] text-xs'>Trusted by 47,000+ creators</div>
                            <div className='flex-1'></div>
                            <div className='w-px bg-[#3a3a3a]'></div>
                            <div className='flex-1'></div>
                            <div className='flex items-center gap-1.5 text-[#6b6b6b] text-xs11'>
                                <div className='text-[#ffb800] text-xs'>★★★★★</div>
                                <div className='text-[#8a8a8a] font-medium'>4.8</div>
                                <div className=''>TrustPilot</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
